import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/runtime/JsonSchemaForm.tsx
// Zero-dep, accessible JSON Schema form. Supports string/number/boolean with
// enum, format=password|email|url|cron, and default values. Built to be kid-friendly:
// labels are plain English and error messages say what's wrong.
import { useState, useEffect } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
export default function JsonSchemaForm({ schema, value, onSave, saveLabel = 'Save changes' }) {
    const [local, setLocal] = useState(value);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => { setLocal(value); }, [value]);
    const set = (k, v) => setLocal(prev => ({ ...prev, [k]: v }));
    const entries = Object.entries(schema.properties);
    const required = schema.required ?? [];
    async function submit(e) {
        e.preventDefault();
        setError(null);
        // Simple required-field check — kid-friendly messages.
        for (const key of required) {
            const v = local[key];
            if (v === undefined || v === null || v === '') {
                setError(`Please fill in "${schema.properties[key].title ?? key}" — it's required.`);
                return;
            }
        }
        setSaving(true);
        try {
            await onSave(local);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        }
        catch (e) {
            setError(e?.message ?? 'Could not save.');
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsxs("form", { onSubmit: submit, className: "space-y-4", children: [entries.map(([key, prop]) => {
                const label = prop.title ?? key;
                const val = (local[key] ?? prop.default ?? (prop.type === 'boolean' ? false : ''));
                const isReq = required.includes(key);
                if (prop.type === 'boolean') {
                    return (_jsxs("label", { className: "flex items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-200", children: [_jsx("input", { type: "checkbox", className: "mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500", checked: Boolean(val), onChange: e => set(key, e.target.checked) }), _jsxs("span", { children: [_jsxs("span", { className: "block text-sm font-medium text-slate-900", children: [label, isReq ? ' *' : ''] }), prop.description && _jsx("span", { className: "mt-0.5 block text-xs text-slate-500", children: prop.description })] })] }, key));
                }
                if (prop.enum && prop.enum.length) {
                    return (_jsxs("label", { className: "block", children: [_jsxs("span", { className: "mb-1 block text-sm font-medium text-slate-900", children: [label, isReq ? ' *' : ''] }), prop.description && _jsx("span", { className: "mb-1 block text-xs text-slate-500", children: prop.description }), _jsxs("select", { value: String(val ?? ''), onChange: e => set(key, e.target.value), className: "block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500", children: [_jsx("option", { value: "", disabled: true, children: "Choose one\u2026" }), prop.enum.map(opt => _jsx("option", { value: opt, children: opt }, opt))] })] }, key));
                }
                const inputType = prop.format === 'password' ? 'password' :
                    prop.format === 'email' ? 'email' :
                        prop.format === 'url' ? 'url' :
                            prop.type === 'number' ? 'number' :
                                'text';
                return (_jsx(Input, { label: label + (isReq ? ' *' : ''), hint: prop.description, type: inputType, value: val === undefined || val === null ? '' : String(val), onChange: e => set(key, prop.type === 'number' ? Number(e.target.value) : e.target.value) }, key));
            }), error && (_jsx("div", { role: "alert", className: "rounded-lg bg-red-50 px-4 py-3 text-sm text-red-900 ring-1 ring-red-200", children: error })), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { type: "submit", disabled: saving, children: saving ? 'Saving…' : saveLabel }), saved && _jsx("span", { className: "text-sm text-emerald-700", children: "Saved." })] })] }));
}
